import React, { useState, useMemo } from 'react';
import { Edit, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { updateTenantBalance, updateTenantDeposit } from './api';
import { useAppTheme } from '../../contexts/ThemeContext';
// Add this import at the top
import { createPortal } from 'react-dom';

/**
 * UpdateBalanceDeposit
 * Props:
 *   tenant        — tenant object (needs .id, .balance, .securityDeposit)
 *   onUpdated(patch) — called with { balance? | securityDeposit? } after save so parent
 *                      can update its local copy
 *   mode: 'balance' | 'deposit' | 'both' (default)
 */

// z-[200] ensures overlay sits above sidebar (typically z-40) and navbar (typically z-50)
const ModalOverlay = ({ children }) =>
  createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.65)', zIndex: 9999 }}
    >
      {children}
    </div>,
    document.body
  );

const UpdateBalanceDeposit = ({ tenant, onUpdated, mode = 'both' }) => {
  const { palette, Colors } = useAppTheme();
  const bg     = palette.background;
  const txt    = palette.text;
  const border = palette.border;
  const P      = Colors.primary.main;

  const inputClass = `w-full px-4 py-2.5 rounded-lg focus:outline-none focus:ring-2`;
  const inputStyle = { backgroundColor: bg.tertiary, border: `1px solid ${border}`, color: txt.primary };

  // ── balance state ──────────────────────────────────────────────────────────
  const [balanceDialog, setBalanceDialog] = useState(false);
  const [newBalance, setNewBalance]       = useState('');
  const [balanceReason, setBalanceReason] = useState('');
  const [savingBalance, setSavingBalance] = useState(false);
  const [balanceError, setBalanceError]   = useState(null);

  // ── deposit state ──────────────────────────────────────────────────────────
  const [depositDialog, setDepositDialog] = useState(false);
  const [newDeposit, setNewDeposit]       = useState('');
  const [depositReason, setDepositReason] = useState('');
  const [savingDeposit, setSavingDeposit] = useState(false);
  const [depositError, setDepositError]   = useState(null);

  // ── balance change calculation ─────────────────────────────────────────────
  const currentBalance = Number(tenant.balance ?? 0);

  const balanceDiff = useMemo(() => {
    const parsed = parseFloat(newBalance);
    if (isNaN(parsed)) return null;
    return parsed - currentBalance;
  }, [newBalance, currentBalance]);

  const BalanceChangePanel = () => {
    if (balanceDiff === null) return null;

    const isCredit  = balanceDiff > 0;
    const isDebit   = balanceDiff < 0;
    const noChange  = balanceDiff === 0;
    const newBal    = parseFloat(newBalance);

    const color = noChange
      ? txt.muted
      : isCredit
        ? Colors.status.success
        : Colors.status.error;

    const bgColor = noChange
      ? `${txt.muted}10`
      : isCredit
        ? `${Colors.status.success}12`
        : `${Colors.status.error}12`;

    const borderColor = noChange
      ? `${txt.muted}30`
      : isCredit
        ? `${Colors.status.success}35`
        : `${Colors.status.error}35`;

    const Icon = noChange ? Minus : isCredit ? TrendingUp : TrendingDown;

    const label = noChange
      ? 'No change to balance'
      : isCredit
        ? `Credit of KES +${Math.abs(balanceDiff).toLocaleString(undefined, { minimumFractionDigits: 2 })}`
        : `Debit of KES −${Math.abs(balanceDiff).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

    const sublabel = noChange
      ? 'The balance will remain the same.'
      : isCredit
        ? 'Positive adjustment — this will increase the tenant\'s credit.'
        : 'Negative adjustment — this will increase the amount owed.';

    return (
      <div
        className="rounded-xl p-4 mb-5"
        style={{ backgroundColor: bgColor, border: `1px solid ${borderColor}` }}
      >
        {/* Main change badge */}
        <div className="flex items-center gap-2 mb-3">
          <div
            className="flex items-center justify-center w-8 h-8 rounded-full flex-shrink-0"
            style={{ backgroundColor: `${color}20` }}
          >
            <Icon className="w-4 h-4" style={{ color }} />
          </div>
          <span className="text-base font-bold" style={{ color }}>
            {label}
          </span>
        </div>

        <p className="text-xs mb-3" style={{ color: txt.muted }}>{sublabel}</p>

        {/* Before / After row */}
        <div
          className="grid grid-cols-3 gap-2 rounded-lg p-3"
          style={{ backgroundColor: bg.tertiary, border: `1px solid ${border}` }}
        >
          <div className="text-center">
            <p className="text-xs mb-1" style={{ color: txt.muted }}>Before</p>
            <p
              className="text-sm font-semibold"
              style={{ color: currentBalance < 0 ? Colors.status.error : txt.primary }}
            >
              {currentBalance < 0 ? '−' : ''}KES {Math.abs(currentBalance).toLocaleString(undefined, { minimumFractionDigits: 2 })}
            </p>
          </div>

          <div className="flex items-center justify-center">
            <div
              className="px-2 py-0.5 rounded-full text-xs font-medium"
              style={{
                backgroundColor: noChange ? `${txt.muted}20` : `${color}20`,
                color: noChange ? txt.muted : color,
              }}
            >
              {noChange ? '=' : isCredit ? `+${Math.abs(balanceDiff).toLocaleString()}` : `−${Math.abs(balanceDiff).toLocaleString()}`}
            </div>
          </div>

          <div className="text-center">
            <p className="text-xs mb-1" style={{ color: txt.muted }}>After</p>
            <p
              className="text-sm font-bold"
              style={{ color: newBal < 0 ? Colors.status.error : Colors.status.success }}
            >
              {newBal < 0 ? '−' : ''}KES {Math.abs(newBal).toLocaleString(undefined, { minimumFractionDigits: 2 })}
            </p>
          </div>
        </div>

        {!noChange && (
          <p className="text-xs mt-2 text-center" style={{ color: txt.muted }}>
            This adjustment will be recorded in the audit log.
          </p>
        )}
      </div>
    );
  };

  // ── helpers ────────────────────────────────────────────────────────────────
  const CurrentRow = ({ label, value }) => (
    <div
      className="flex items-center justify-between p-3.5 rounded-lg mb-5"
      style={{ backgroundColor: bg.tertiary, border: `1px solid ${border}` }}
    >
      <span className="text-sm" style={{ color: txt.secondary }}>{label}</span>
      <span className="text-sm font-bold" style={{ color: txt.primary }}>{value}</span>
    </div>
  );

  const ErrorBox = ({ msg }) => (
    <div
      className="p-3 rounded-lg text-sm mb-4"
      style={{
        backgroundColor: `${Colors.status.error}15`,
        border: `1px solid ${Colors.status.error}40`,
        color: Colors.status.error,
      }}
    >
      {msg}
    </div>
  );

  // ── balance handlers ───────────────────────────────────────────────────────
  const openBalance = () => {
    setNewBalance(String(currentBalance));
    setBalanceReason('');
    setBalanceError(null);
    setBalanceDialog(true);
  };

  const handleSaveBalance = async () => {
    const val = parseFloat(newBalance);
    if (isNaN(val)) { setBalanceError('Enter a valid number'); return; }
    if (!balanceReason.trim()) { setBalanceError('Please provide a reason for this adjustment'); return; }
    setSavingBalance(true); setBalanceError(null);
    try {
      await updateTenantBalance(tenant.id, { new_balance: val, reason: balanceReason.trim() });
      setBalanceDialog(false);
      if (onUpdated) onUpdated({ balance: val });
    } catch (e) {
      setBalanceError(typeof e === 'string' ? e : 'Failed to update balance');
    } finally {
      setSavingBalance(false);
    }
  };

  // ── deposit handlers ───────────────────────────────────────────────────────
  const openDeposit = () => {
    setNewDeposit(String(tenant.securityDeposit?.total ?? tenant.securityDeposit ?? 0));
    setDepositReason('');
    setDepositError(null);
    setDepositDialog(true);
  };

  const handleSaveDeposit = async () => {
    const val = parseFloat(newDeposit);
    if (isNaN(val) || val < 0) { setDepositError('Enter a valid non-negative number'); return; }
    if (!depositReason.trim()) { setDepositError('Please provide a reason for this adjustment'); return; }
    setSavingDeposit(true); setDepositError(null);
    try {
      await updateTenantDeposit(tenant.id, { deposit: val, reason: depositReason.trim() });
      setDepositDialog(false);
      if (onUpdated) onUpdated({ securityDeposit: val });
    } catch (e) {
      setDepositError(typeof e === 'string' ? e : 'Failed to update deposit');
    } finally {
      setSavingDeposit(false);
    }
  };

  // ── render ─────────────────────────────────────────────────────────────────
  return (
    <>
      {(mode === 'balance' || mode === 'both') && (
        <button
          onClick={openBalance}
          title="Edit balance"
          className="p-1 rounded transition-colors hover:opacity-70"
          style={{ color: Colors.status.success }}
        >
          <Edit className="w-3.5 h-3.5" />
        </button>
      )}

      {(mode === 'deposit' || mode === 'both') && (
        <button
          onClick={openDeposit}
          title="Edit deposit"
          className="p-1 rounded transition-colors hover:opacity-70"
          style={{ color: Colors.accent.purple }}
        >
          <Edit className="w-3.5 h-3.5" />
        </button>
      )}

      {/* ── Edit Balance modal ───────────────────────────────────────────── */}
      {balanceDialog && (
        <ModalOverlay>
          <div
            className="rounded-2xl w-full max-w-2xl p-8 shadow-2xl"
            style={{ backgroundColor: bg.secondary, border: `1px solid ${border}` }}
          >
            <h3 className="text-2xl font-bold mb-6" style={{ color: txt.primary }}>
              Edit Current Balance
            </h3>

            <CurrentRow
              label="Current balance"
              value={`${currentBalance < 0 ? '−' : ''}KES ${Math.abs(currentBalance).toLocaleString(undefined, { minimumFractionDigits: 2 })}`}
            />

            {balanceError && <ErrorBox msg={balanceError} />}

            <div className="mb-5">
              <label className="block text-sm font-medium mb-2" style={{ color: txt.secondary }}>
                New Balance (KES)
              </label>
              <input
                type="number"
                value={newBalance}
                onChange={e => setNewBalance(e.target.value)}
                autoFocus
                className={inputClass}
                style={inputStyle}
              />
              <p className="text-xs mt-1" style={{ color: txt.muted }}>
                Can be negative if tenant owes money
              </p>
            </div>

            {/* Live change panel — only shown when a value is typed */}
            <BalanceChangePanel />

            <div className="mb-7">
              <label className="block text-sm font-medium mb-2" style={{ color: txt.secondary }}>
                Reason <span style={{ color: Colors.status.error }}>*</span>
              </label>
              <textarea
                rows={2}
                value={balanceReason}
                onChange={e => setBalanceReason(e.target.value)}
                placeholder="e.g. Manual correction, error fix…"
                className={inputClass}
                style={{ ...inputStyle, resize: 'none' }}
              />
              <p className="text-xs mt-1" style={{ color: txt.muted }}>
                Saved in audit log with your name and timestamp
              </p>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setBalanceDialog(false)}
                disabled={savingBalance}
                className="px-5 py-2.5 rounded-lg font-medium"
                style={{ backgroundColor: bg.tertiary, color: txt.primary, border: `1px solid ${border}` }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveBalance}
                disabled={savingBalance}
                className="px-5 py-2.5 text-white rounded-lg font-medium"
                style={{ backgroundColor: P }}
              >
                {savingBalance ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </div>
        </ModalOverlay>
      )}

      {/* ── Edit Deposit modal ───────────────────────────────────────────── */}
      {depositDialog && (
        <ModalOverlay>
          <div
            className="rounded-2xl w-full max-w-2xl p-8 shadow-2xl"
            style={{ backgroundColor: bg.secondary, border: `1px solid ${border}` }}
          >
            <h3 className="text-2xl font-bold mb-6" style={{ color: txt.primary }}>
              Edit Security Deposit
            </h3>

            <CurrentRow
              label="Current deposit"
              value={`KES ${Number(tenant.securityDeposit?.total ?? tenant.securityDeposit ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`}
            />

            {depositError && <ErrorBox msg={depositError} />}

            <div className="mb-5">
              <label className="block text-sm font-medium mb-2" style={{ color: txt.secondary }}>
                New Amount (KES)
              </label>
              <input
                type="number"
                min="0"
                value={newDeposit}
                onChange={e => setNewDeposit(e.target.value)}
                autoFocus
                className={inputClass}
                style={inputStyle}
              />
            </div>

            <div className="mb-7">
              <label className="block text-sm font-medium mb-2" style={{ color: txt.secondary }}>
                Reason <span style={{ color: Colors.status.error }}>*</span>
              </label>
              <textarea
                rows={2}
                value={depositReason}
                onChange={e => setDepositReason(e.target.value)}
                placeholder="e.g. Deposit top-up, partial refund agreed…"
                className={inputClass}
                style={{ ...inputStyle, resize: 'none' }}
              />
              <p className="text-xs mt-1" style={{ color: txt.muted }}>
                Saved in audit log with your name and timestamp
              </p>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDepositDialog(false)}
                disabled={savingDeposit}
                className="px-5 py-2.5 rounded-lg font-medium"
                style={{ backgroundColor: bg.tertiary, color: txt.primary, border: `1px solid ${border}` }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveDeposit}
                disabled={savingDeposit}
                className="px-5 py-2.5 text-white rounded-lg font-medium"
                style={{ backgroundColor: Colors.accent.purple }}
              >
                {savingDeposit ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </div>
        </ModalOverlay>
      )}
    </>
  );
};

export default UpdateBalanceDeposit;